import { Component, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';
import { DataTableDirective } from 'angular-datatables';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { environment } from 'src/environments/environment';
import { DialogService } from 'src/app/utils/services/dialog.service';
import { SectiuniService } from './sectiuni.service';
import { AddSectiuneComponent } from './add-sectiune/add-sectiune.component';
import { EditSectiuneComponent } from './edit-sectiune/edit-sectiune.component';
import { ISectiune } from './sectiuni.interface';

@Component({
  selector: 'app-sectiuni',
  templateUrl: './sectiuni.component.html',
  styleUrls: ['./sectiuni.component.scss'],
})
export class SectiuniComponent {
  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  isLoading = false;

  dtOptions: any = {};
  dtTrigger: Subject<any> = new Subject<any>();

  dtLink =
    environment.interop.basePath +
    environment.interop.platform.nomenclatoare.section.getSections +
    '-dt';

  dataTablesParameters: any = {
    data: {
      key: 'parameter',
    },
  };

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private sectiuniService: SectiuniService
  ) {}

  ngOnInit(): void {
    this.renderDT();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next('');
  }

  renderDT() {
    this.isLoading = true;
    this.dtOptions = {
      serverSide: true,
      responsive: true,
      pageLength: 25,
      searching: false,
      ajax: (dataTablesParameters: any, callback: any) => {

        this.http
          .post<DataTablesResponse>(
            this.dtLink, // datatable link, we need this
            dataTablesParameters // parameters for filtering, send here
          )
          .subscribe((resp: any) => {
            this.isLoading = false;
            callback({
              recordsTotal: resp.data.recordsTotal,
              recordsFiltered: resp.data.recordsFiltered,
              data: resp.data.data,
            });
          });
      },
      language: environment.config.romanianLanguageDataTable,
      columns: [
        {
          title: 'ID',
          data: 'id',
        },
        {
          title: 'Denumire',
          data: 'name',
        },
        {
          title: 'Slug',
          data: 'slug',
        },
        {
          title: 'Acțiuni',
          data: null,
          className: 'all',
          orderable: false,
          render: () => {
            return `
            <div class="d-flex align-center justify-content-center">
              <button class="action-btn edit">
                <img class="btn-img" src="/assets/icons/edit.svg" alt="edit"/>
              </button>
              <button class="action-btn delete">
                <img class="btn-img" src="/assets/icons/delete.svg" alt="delete"/>
              </button>
              </div>
            `;
          },
        },
      ],
      rowCallback: (row: Node, data: any[] | any) => {
        const lastTd = (row as HTMLElement).querySelector('td:last-child');
        const editIcon = lastTd?.querySelector('.edit');
        const deleteIcon = lastTd?.querySelector('.delete');

        if (editIcon) {
          editIcon.addEventListener('click', () => {
            this.openEditDialog(data);
          });
        }

        if (deleteIcon) {
          deleteIcon.addEventListener('click', () => {
            const { id } = data;
            this.deleteSection(id);
          });
        }

        return row;
      },
    };
  }

  rerenderDT(): void {
    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      // Destroy the table first
      dtInstance.destroy();
      // Call the dtTrigger to rerender again
      this.dtTrigger.next('');
    });
  }

  deleteSection(sectionId: number) {
    Swal.fire({
      title: 'Sunteți sigur?',
      text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Confirm!',
      cancelButtonText: 'Cancel',
      showLoaderOnConfirm: true,
    }).then((result) => {
      if (result.isConfirmed) {
        this.sectiuniService.deleteSection(sectionId).subscribe(() => {
          this.rerenderDT();
          Swal.fire({
            title: 'Șters!',
            text: 'Secțiune ștearsă cu succes.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        });
      }
    });
  }

  openAddDialog() {
    const dialogRef = this.dialog.open(AddSectiuneComponent, {
      ...this.dialogService.getDialogConfig(),
    });

    dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
      if (success) {
        this.rerenderDT();
      }
    });
  }

  openEditDialog(data: ISectiune) {
    const dialogRef = this.dialog.open(EditSectiuneComponent, {
      ...this.dialogService.getDialogConfig(),
      data,
    });

    dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
      if (success) {
        this.rerenderDT();
      }
    });
  }
}
